import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Modal, Button, Form, Container } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaDatabase } from 'react-icons/fa';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function CRTAttributesAdmin() {
  const { token } = useContext(AuthContext);
  const [attributes, setAttributes] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState({ display_name: '', name: '', values: [] });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (token) fetchAttributes(); }, [token]);

  const fetchAttributes = async () => {
    try {
      const res = await fetch(`${STORE_API_URL}/crtattributes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAttributes(data);
    } catch (err) { toast.error('Error fetching attributes.'); }
  };

  const openModal = (attr = null) => {
    setSelectedAttribute(attr || { display_name: '', name: '', values: [] });
    setIsEditing(!!attr);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAttribute({ display_name: '', name: '', values: [] });
  };

  const handleSave = async () => {
    const method = isEditing ? 'PUT' : 'POST';
    const endpoint = isEditing ? `${STORE_API_URL}/crtattributes/${selectedAttribute._id}` : `${STORE_API_URL}/crtattributes`;

    try {
      const body = { ...selectedAttribute, name: selectedAttribute.name.toLowerCase() };
      await fetch(endpoint, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      toast.success(isEditing ? 'Attribute updated!' : 'Attribute added!');
      fetchAttributes();
      closeModal();
    } catch (err) { toast.error('Failed to save attribute.'); }
  };

  const handleCRTDatabase = () => {
    navigate('/superadmin/crts');
  };

  return (
    <Container>
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">CRT Attribute</h2>
        <div className="mb-2">
          <Button variant="success" onClick={() => openModal(null)}><FaPlus /> Attribute</Button>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <button className="btn btn-primary me-2 mb-2" onClick={handleCRTDatabase}><FaDatabase style={{ marginRight: '5px' }} />CRT Database</button>
        </div>
      </div>
      <Table striped bordered hover>
        <thead><tr><th>Display Name</th><th>Values</th><th>Actions</th></tr></thead>
        <tbody>
          {Object.keys(attributes).map(name => (
            <tr key={name}>
              <td>{attributes[name].display_name}</td>
              <td>{Array.isArray(attributes[name].values) ? attributes[name].values.join(', ') : attributes[name].values}</td>
              <td>
                <Button variant="warning" size="sm" onClick={() => openModal(attributes[name])}><FaEdit /></Button>{' '}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={isModalOpen} onHide={closeModal}>
        <Modal.Header closeButton><Modal.Title>{isEditing ? 'Edit Attribute' : 'Add Attribute'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Display Name</Form.Label>
              <Form.Control value={selectedAttribute.display_name} onChange={(e) => setSelectedAttribute({ ...selectedAttribute, display_name: e.target.value })} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control value={selectedAttribute.name} onChange={(e) => setSelectedAttribute({ ...selectedAttribute, name: e.target.value.toLowerCase() })} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Values</Form.Label>
              {selectedAttribute.values.map((val, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    value={val}
                    onChange={(e) => {
                      const newValues = [...selectedAttribute.values];
                      newValues[idx] = e.target.value;
                      setSelectedAttribute({ ...selectedAttribute, values: newValues });
                    }}
                  />
                  <Button variant="danger" size="sm" onClick={() => {
                    const newValues = selectedAttribute.values.filter((_, i) => i !== idx);
                    setSelectedAttribute({ ...selectedAttribute, values: newValues });
                  }}>Remove</Button>
                </div>
              ))}
              <Button onClick={() => setSelectedAttribute({ ...selectedAttribute, values: [...selectedAttribute.values, ''] })}>Add Value</Button>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Close</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CRTAttributesAdmin;
